// frontend/src/services/api.js
// All REST API calls — single source of truth for backend communication
import socketOperator from './socketOperator';   // MP-STAB-03 — cerrar sesión activa (socket)

const API_BASE = '';

async function request(method, path, body = null) {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  // La superficie de consola (operator/upload/export) exige sesión operativa
  // (device-status — H1 por política). Adjuntamos el token si existe; las rutas
  // públicas (tienda) lo ignoran. Nunca autoridad local: solo transporte.
  const token = (() => { try { return localStorage.getItem('kf25_operator_token'); } catch { return null; } })();
  if (token) opts.headers['Authorization'] = 'Bearer ' + token;
  if (body) opts.body = JSON.stringify(body);
  
  const res = await fetch(`${API_BASE}${path}`, opts);
  const data = await res.json();
  
  if (!res.ok) {
    console.error(`[API] Error ${method} ${path}:`, data.error || res.statusText);
    const err = new Error(data.error || `HTTP ${res.status}`);
    if (data.code) err.code = data.code;   // KF25-AUTH-02: el backend comunica la CAUSA tipada
    // 401 en superficie operativa = sesión muerta/ausente → rutina global de
    // expulsión (mismo contrato que requestAuth); el resolver re-ejecuta.
    if (res.status === 401 && data.code === 'SESSION_REQUIRED' || res.status === 401 && data.code === 'INVALID_TOKEN') {
      handleSessionExpired(data.code === 'INVALID_TOKEN' ? 'reauth_required' : 'not_found');
    }
    throw err;
  }
  
  return data;
}

// === CARTS ===
export async function createCart(items) {
  return request('POST', '/api/carts', { items });
}

export async function getCart(cartId) {
  return request('GET', `/api/carts/${cartId}`);
}

export async function listCarts() {
  return request('GET', '/api/carts');
}

export async function addItemToCart(cartId, productCode, name, price) {
  return request('POST', `/api/carts/${cartId}/items`, { productCode, name, price });
}

export async function removeItemFromCart(cartId, productCode) {
  return request('DELETE', `/api/carts/${cartId}/items/${productCode}`);
}

export async function updateCartItems(cartId, updates) {
  return request('PATCH', `/api/carts/${cartId}/items`, { updates });
}

export async function checkoutCart(cartId, clientName, clientPhone) {
  return request('POST', `/api/carts/${cartId}/checkout`, { clientName, clientPhone });
}

export async function getBlockedProducts() {
  return request('GET', '/api/carts/blocked');
}

// === PRODUCTS ===
export async function listProducts() {
  return request('GET', '/api/products');
}

export async function getProduct(code, options = {}) {
  // PRINCIPIO KF25-ENT-01/02 (2026-08-03): el detalle resuelve la ENTIDAD por código.
  // includeHidden=true solo se usa en modo operador; el backend exige token válido
  // (x-operator-token) para devolver productos ocultos (visibilidad autorizada).
  const qs = options.includeHidden ? '?includeHidden=true' : '';
  const headers = { 'Content-Type': 'application/json' };
  if (options.includeHidden) {
    const token = localStorage.getItem('kf25_operator_token');
    if (token) headers['x-operator-token'] = token;
  }
  const res = await fetch(`${API_BASE}/api/products/${code}${qs}`, { headers });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
}

// === AUTH ===
export async function operatorLogin(password) {
  return request('POST', '/api/auth/operator', { password });
}

export async function validateToken(token) {
  return request('POST', '/api/auth/validate', { token });
}

// === DEVICE (T3) — autenticación de dispositivo autorizado ===
export async function deviceLogin(deviceId, deviceSecret, cartId) {
  return request('POST', '/api/auth/device', { deviceId, deviceSecret, cartId });
}

export async function deviceRenew(deviceId, deviceSecret, authzId) {
  return request('POST', '/api/auth/device/renew', { deviceId, deviceSecret, authzId });
}

// === CONTRATO ÚNICO DE SESIÓN (MP-STAB-03) ===
// sessionStatus: consulta el estado real de una sesión (autoridad: backend adminSession).
// Contrato puro: describe lo que existe (operator | admin-session | device-status), nunca promueve.
export async function sessionStatus(token) {
  if (!token) return { status: 'not_found', session: null };
  try {
    const res = await fetch(`${API_BASE}/api/auth/session/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    const data = await res.json();
    return data.status ? data : { status: 'not_found', session: null };
  } catch {
    return null;   // error de red → el chequeo proactivo NO dispara la rutina (spec §4.3)
  }
}

// handleSessionExpired: RUTINA GLOBAL ÚNICA — NO conoce pantallas ni rutas.
// Responsabilidades: (1) limpiar sesión local, (2) cerrar sesión activa (socket),
// (3) informar (evento global con status), (4) devolver control al mecanismo oficial
// de autenticación (App.tsx escucha el evento; el guard de rutas redirige).
export function handleSessionExpired(status = 'reauth_required') {
  try {
    localStorage.removeItem('kf25_operator_mode');
    localStorage.removeItem('kf25_operator_token');
    localStorage.removeItem('kf25_admin_id');
    sessionStorage.removeItem('kf25_op_token');
  } catch { /* storage no disponible — continuar */ }
  try {
    socketOperator.disconnect();   // cierra la sesión activa (no conoce pantallas)
  } catch { /* socket no inicializado — continuar */ }
  window.dispatchEvent(new CustomEvent('kf25:session-expired', { detail: { status } }));
}

// === DEVICES ADMIN (STAB-01) — módulo permanente de administración de dispositivos ===
// Todas las llamadas requieren sesión administrativa (Bearer token del operador).
export async function requestAuth(method, path, body = null, token) {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (token) opts.headers.Authorization = `Bearer ${token}`;
  if (body) opts.body = JSON.stringify(body);

  const res = await fetch(`${API_BASE}${path}`, opts);
  if (res.status === 401) {
    handleSessionExpired('reauth_required');   // MP-STAB-03 — única rutina, sin lógica por pantalla
  }
  const data = await res.json();

  if (!res.ok) {
    console.error(`[API] Error ${method} ${path}:`, data.error || res.statusText);
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
}

export async function listDevices(token) {
  return requestAuth('GET', '/api/auth/devices', null, token);
}

export async function listAdmins(token) {
  return requestAuth('GET', '/api/auth/admins', null, token);
}

export async function getDeviceDetail(deviceId, token) {
  return requestAuth('GET', `/api/auth/devices/${deviceId}`, null, token);
}

export async function getDeviceHistory(deviceId, token) {
  return requestAuth('GET', `/api/auth/devices/${deviceId}/history`, null, token);
}

export async function reactivateDevice(deviceId, reason, token) {
  return requestAuth('POST', `/api/auth/devices/${deviceId}/reactivate`, { reason }, token);
}

export async function createEnrollment(destinatarioId, label, token) {
  return requestAuth('POST', '/api/auth/enrollments', { destinatarioId, label }, token);
}

export async function registerDelivery(enrollmentId, provider, target, token) {
  return requestAuth('POST', `/api/auth/enrollments/${enrollmentId}/deliveries`, { provider, target }, token);
}

// Públicos (el link ES la credencial — sin token)
export async function resolveEnrollment(enrollmentId) {
  return request('GET', `/api/enrollments/${enrollmentId}`);
}

export async function redeemEnrollment(enrollmentId, label) {
  return request('POST', `/api/enrollments/${enrollmentId}/redeem`, { label });
}

export async function getReplaceCode(deviceId, token) {
  return requestAuth('POST', `/api/auth/devices/${deviceId}/replace-code`, null, token);
}

// === OPERATOR ===
export async function searchOperator(q) {
  return request('GET', `/api/operator/search?q=${encodeURIComponent(q)}`);
}

export async function getDashboard(params = {}) {
  const query = params.scope ? `?scope=${params.scope}` : '';
  return request('GET', `/api/operator/dashboard${query}`);
}

// === CLIENTS ===
export async function getClient(phone) {
  return request('GET', `/api/clients/${phone}`);
}

export async function updateClient(phone, data) {
  return request('PATCH', `/api/clients/${phone}`, data);
}

export async function blockProduct(code) {
  return request('POST', `/api/operator/products/${code}/block`);
}

export async function unblockProduct(code) {
  return request('POST', `/api/operator/products/${code}/unblock`);
}

export async function hideProduct(code) {
  return request('POST', `/api/operator/products/${code}/hide`);
}

export async function updateProductStatus(code, status) {
  return request('PATCH', `/api/operator/products/${code}/status`, { status });
}

// ─── Helper: formdata upload (multipart) ───
async function uploadFormData(url, formData) {
  const headers = {};
  // Superficie de consola (product:manage — H1 por política): adjuntar el token
  // de sesión operativa (device-status). Sin él → 401 (gate del Policy Engine).
  const token = (() => { try { return localStorage.getItem('kf25_operator_token'); } catch { return null; } })();
  if (token) headers['Authorization'] = 'Bearer ' + token;
  const res = await fetch(`${API_BASE}${url}`, {
    method: 'POST',
    headers,
    body: formData
  });

  // Defensive: check content-type before parsing JSON
  // (nginx can return 413 HTML if body is too large)
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await res.text();
    if (res.status === 413) {
      throw new Error('El archivo es demasiado grande. El límite es 50MB por archivo.');
    }
    if (res.status === 502) {
      throw new Error('Error de conexión con el servidor. Intenta de nuevo.');
    }
    throw new Error(`Error del servidor (${res.status}). Intenta de nuevo.`);
  }

  const data = await res.json();
  if (!res.ok) {
    console.error(`[API] Error POST ${url}:`, data.error || res.statusText);
    throw new Error(data.error || `Error del servidor (${res.status})`);
  }
  return data;
}

// === UPLOAD (FASE 1A.1 + 1A.2) ===
// Capacidades de media: ÚNICA fuente = Media Capabilities Registry (backend).
// El frontend NUNCA define listas de formatos; las deriva de /api/upload/limits.
export async function fetchMediaLimits() {
  return request('GET', '/api/upload/limits');
}

export async function validateUpload(formData) {
  return uploadFormData('/api/upload/validate', formData);
}

export async function createProduct(formData) {
  return uploadFormData('/api/upload/product', formData);
}

export async function addProductMedia(code, formData) {
  return uploadFormData(`/api/upload/media/${code}`, formData);
}
